/* eslint-disable import/prefer-default-export */
import React, { useRef, useState, useEffect } from 'react'
import * as styles from '../date.pickers.styl'
import {
  START_DATE,
  getInputValue,
  END_DATE,
  FocusedInput,
  OnDatesChangeProps,
  parseDate
} from '@datepicker-react/hooks'
import TextBox from '../../text.box'
import DateRangeHook from './date.range'
import cx from 'classnames'
import { isEqual } from 'lodash'
import Button from '../../button'
import TimePicker from './time.picker'
import { isMatch, isValid } from 'date-fns'
import { createDateFromString, formatDateTime } from '@src/utils/date.helpers'

export interface DatepickerProps {
  role?: string
  displayFormat?: DisplayFormat
  onFocusChange?: (focusInput: FocusedInput) => void
  startDateInputId?: string
  endDateInputId?: string
  className?: string
  startDatePlaceholderText?: string
  endDatePlaceholderText?: string
  onDatesChanged?: (data: DateRangeData) => void
  onDatesChange?: (data: DateRangeData) => void
  startDate: Date | null
  endDate: Date | null
  focusedInput: FocusedInput
  onClose?: () => void
  applyButtonTitle?: string
}

export interface DateRangeData {
  startDate?: Date
  endDate?: Date
  focusedInput?: FocusedInput
}

export type DisplayFormat = 'yyyy-MM-dd' | 'yyyy-MM-dd hh:mm a'

export const DateRangePicker: React.FC<
  React.PropsWithChildren<DatepickerProps>
> = React.memo(
  ({
    className,
    role,
    startDate,
    endDate,
    onFocusChange,
    focusedInput,
    onDatesChange,
    displayFormat = 'yyyy-MM-dd',
    startDateInputId = 'startDate',
    endDateInputId = 'endDate',
    startDatePlaceholderText,
    endDatePlaceholderText,
    onDatesChanged,
    onClose,
    children,
    applyButtonTitle = 'Apply'
  }: React.PropsWithChildren<DatepickerProps>) => {
    const ref = useRef(null)
    const datepickerWrapperRef = useRef<HTMLDivElement>(null)
    const [state, setState] = useState<DateRangeData>({
      startDate,
      endDate,
      focusedInput
    })
    // const state = { startDate, endDate, focusedInput }

    useEffect(() => {
      if (!isEqual(state, { startDate, endDate, focusedInput })) {
        setState(item => ({
          ...item,
          startDate,
          endDate,
          focusedInput
        }))
      }
    }, [startDate, endDate, focusedInput])

    function handleRangeDates(data: OnDatesChangeProps) {
      if (typeof onDatesChange === 'function') {
        onDatesChange(data)
      } else if (!data.focusedInput) {
        if (!isEqual(data, state)) {
          setState(state => ({
            ...state,
            ...data,
            focusedInput: START_DATE
          }))
        }
      } else if (!isEqual(data, state)) {
        setState(state => ({ ...state, ...data }))
      }
    }

    function handleFocus(focusedInput: FocusedInput) {
      if (typeof onFocusChange === 'function') {
        onFocusChange(focusedInput)
      } else if (!isEqual(focusedInput, state.focusedInput)) {
        setState(item => ({ ...item, focusedInput }))
      }
    }

    function handleDatesChanged() {
      if (onDatesChanged) {
        onDatesChanged(state)
      }
    }

    function onClickOutsideHandler(event: Event) {
      const node = event.target as Node & { id: string }
      const isInputs =
        node.id === startDateInputId || node.id === endDateInputId
      if (
        datepickerWrapperRef &&
        datepickerWrapperRef?.current &&
        !isInputs &&
        !datepickerWrapperRef?.current?.contains(node)
      ) {
        handleFocus(null)
      }
    }

    function isValidDate(date: Date) {
      return isMatch(formatDateTime(date, displayFormat), displayFormat)
    }

    function handleDateChange(value: React.ReactText, name?: string) {
      if (ref && ref.current && ref.current.onDateSelect) {
        const parsedDate = parseDate(
          String(value),
          displayFormat as string,
          new Date()
        )
        if (
          isValidDate(createDateFromString(String(value), displayFormat)) &&
          isValid(parsedDate)
        ) {
          ref.current.onDateSelect(parsedDate)
        }
      }
    }

    function handleStartTime(date: Date) {
      if (isValidDate(date)) {
        ref.current.onDateSelect(date)
      }
    }
    function handleEndTime(date: Date) {
      if (isValidDate(date)) {
        ref.current.onDateSelect(date)
      }
    }

    function handleStartInputChange(value: React.ReactText, name?: string) {
      const startDateValue = getInputValue(state.startDate, displayFormat, '')
      if (value !== startDateValue) {
        handleDateChange(value)
      }
    }

    function handleEndInputChange(value: React.ReactText, name?: string) {
      const endDateValue = getInputValue(state.endDate, displayFormat, '')
      if (value !== endDateValue) {
        handleDateChange(value)
      }
    }

    function onFocusChangeStartDate() {
      handleFocus(START_DATE)
    }
    function onFocusChangeEndDate() {
      handleFocus(!state.startDate ? START_DATE : END_DATE)
    }

    return (
      <div
        role={role ?? `datepicker_range`}
        className={cx(className, styles.datepickerRange)}
        ref={datepickerWrapperRef}
      >
        <div className={styles.datepickerInputs}>
          <i className={cx(styles.iconCalendar, 'material-icons')}>
            date_range
          </i>
          <TextBox
            className={styles.datepickerInput}
            name={startDateInputId}
            id={startDateInputId}
            placeholder={startDatePlaceholderText}
            defaultValue={getInputValue(state.startDate, displayFormat, '')}
            onClick={onFocusChangeStartDate}
            isActive={state.focusedInput === START_DATE}
            activeClassName={styles.active}
            onChange={handleStartInputChange}
          />
          <span className={styles.separator}>&harr;</span>
          <TextBox
            className={styles.datepickerInput}
            name={endDateInputId}
            placeholder={endDatePlaceholderText}
            id={endDateInputId}
            defaultValue={getInputValue(state.endDate, displayFormat, '')}
            onClick={onFocusChangeEndDate}
            activeClassName={styles.active}
            isActive={state.focusedInput === END_DATE}
            onChange={handleEndInputChange}
          />
        </div>
        {state.focusedInput !== null && (
          <DateRangeHook
            focusedInput={state.focusedInput}
            startDate={state.startDate}
            endDate={state.endDate}
            onClose={onClose}
            onClickOutsideHandler={onClickOutsideHandler}
            onDatesChange={handleRangeDates}
            ref={ref}
          >
            {displayFormat === 'yyyy-MM-dd hh:mm a' && (
              <>
                <TimePicker
                  name="start_time_picker"
                  disabled={!state.startDate}
                  onClick={onFocusChangeStartDate}
                  onDateSelect={handleStartTime}
                  value={state.startDate}
                />
                <TimePicker
                  name="end_time_picker"
                  disabled={!state.endDate}
                  defaultValue={state.startDate}
                  onClick={onFocusChangeEndDate}
                  onDateSelect={handleEndTime}
                  value={state.endDate}
                />
              </>
            )}
          </DateRangeHook>
        )}
        {onDatesChanged && (
          <Button
            className={styles.buttonOk}
            title={applyButtonTitle}
            onClick={handleDatesChanged}
          />
        )}
      </div>
    )
  }
)
